
import { useParams, Link } from 'react-router-dom';
import { tours } from '../../mocks/tours';
import TourHero from './components/TourHero';
import TourInfo from './components/TourInfo';
import TourItinerary from './components/TourItinerary';
import TourReviews from './components/TourReviews';
import TourBooking from './components/TourBooking';

export default function TourDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const tour = tours.find(t => t.slug === slug);

  if (!tour) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏝️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">투어를 찾을 수 없습니다</h2>
          <p className="text-gray-500 mb-6">요청하신 투어 상품이 존재하지 않습니다.</p>
          <Link to="/" className="px-6 py-3 bg-[#00BCD4] text-white font-semibold rounded-lg hover:bg-[#00ACC1] transition-colors cursor-pointer whitespace-nowrap">
            홈으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const otherTours = tours.filter(t => t.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                alt="봉구 투어 로고"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-gray-900">봉구 투어</span>
            </Link>

            <div className="flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                홈
              </Link>
              <a href="#overview" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                투어 소개
              </a>
              <a href="#itinerary" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                상세 일정
              </a>
              <a href="#reviews" className="text-sm font-medium text-gray-700 hover:text-[#00BCD4] transition-colors cursor-pointer whitespace-nowrap">
                후기
              </a>
              <a href="#booking" className="px-6 py-2.5 bg-[#1A237E] text-white text-sm font-semibold rounded-full hover:bg-[#0D47A1] transition-colors cursor-pointer whitespace-nowrap">
                예약하기
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <TourHero
        title={tour.title}
        subtitle={tour.subtitle}
        badge={tour.badge}
        heroImage={tour.heroImage}
        gallery={tour.gallery}
        rating={tour.rating}
        reviewCount={tour.reviewCount}
        price={tour.price}
        originalPrice={tour.originalPrice}
      />

      {/* Info */}
      <div id="overview">
        <TourInfo
          description={tour.description}
          highlights={tour.highlights}
          includes={tour.includes}
          excludes={tour.excludes}
        />
      </div>

      {/* Itinerary */}
      <div id="itinerary">
        <TourItinerary itinerary={tour.itinerary} />
      </div>

      {/* Reviews */}
      <div id="reviews">
        <TourReviews reviews={tour.reviews} rating={tour.rating} reviewCount={tour.reviewCount} />
      </div>

      {/* Booking */}
      <div id="booking">
        <TourBooking tourTitle={tour.title} price={tour.price} />
      </div>

      {/* Other Tours */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">다른 인기 투어</h2>
            <p className="text-gray-500 mt-2">이런 투어도 함께 확인해보세요</p>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {otherTours.map((t) => (
              <Link
                key={t.slug}
                to={`/tour/${t.slug}`}
                className="group relative rounded-2xl overflow-hidden cursor-pointer h-[340px] block"
              >
                <img src={t.heroImage} alt={t.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                {t.badge && (
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-[#FF6B6B] text-white text-xs font-bold rounded-full whitespace-nowrap">{t.badge}</span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="text-xl font-bold mb-1">{t.title}</h3>
                  <p className="text-sm text-white/80 mb-3">{t.subtitle}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-[#FFB300] text-sm"></i>
                      <span className="text-sm font-medium">{t.rating}</span>
                      <span className="text-xs text-white/60">({t.reviewCount})</span>
                    </div>
                    <span className="text-lg font-bold">₱{t.price.toLocaleString()}~</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/" className="px-8 py-3 bg-white text-[#00BCD4] font-semibold rounded-lg border-2 border-[#00BCD4] hover:bg-[#00BCD4] hover:text-white transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2">
              <i className="ri-arrow-left-line"></i>
              전체 투어 보기
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-3xl p-12">
            <div className="grid grid-cols-5 gap-12 mb-12">
              <div className="col-span-1">
                <Link to="/" className="flex items-center gap-2 mb-4 cursor-pointer">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8d0e19d0-7bfb-4b2d-af00-cea6f100e1c2.png"
                    alt="봉구 투어"
                    className="h-10 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900">봉구 투어</span>
                </Link>
                <p className="text-sm text-gray-600 leading-relaxed">
                  필리핀 세부 현지 여행 전문<br />안전한 투어 보장
                </p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#tours">TOURS</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {tours.slice(0, 4).map((t) => (
                    <li key={t.slug}>
                      <Link to={`/tour/${t.slug}`} className="hover:text-[#00BCD4] transition-colors cursor-pointer">{t.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#tours">MORE TOURS</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {tours.slice(4).map((t) => (
                    <li key={t.slug}>
                      <Link to={`/tour/${t.slug}`} className="hover:text-[#00BCD4] transition-colors cursor-pointer">{t.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4"><a href="/#contact">SUPPORT</a></h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><a href="/#contact" className="hover:text-[#00BCD4] transition-colors cursor-pointer">문의하기</a></li>
                  <li><a href="/#pricing" className="hover:text-[#00BCD4] transition-colors cursor-pointer">가격 안내</a></li>
                  <li><a href="/#reviews" className="hover:text-[#00BCD4] transition-colors cursor-pointer">고객 후기</a></li>
                </ul>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-4">FOLLOW US</h4>
                <div className="flex gap-3">
                  <a href="#" className="w-10 h-10 bg-[#FFE500] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-kakao-talk-fill text-[#3C1E1E] text-xl"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#515BD4] rounded-lg flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer">
                    <i className="ri-instagram-line text-white text-xl"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8 flex items-center justify-between">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>&copy; 2025 봉구 투어. All rights reserved.</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-[#00BCD4] transition-colors cursor-pointer">Powered by Readdy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
